import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Loader2, Workflow, Play, Pause, CheckCircle, Clock, AlertCircle, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, parseISO } from 'date-fns';

// Mock data for simulation
const MOCK_WORKFLOWS = [
  { id: '1', name: 'Monthly Safety Audit', status: 'running', progress: 65, current_step: 'Send Reminders', started_date: new Date(Date.now() - 86400000).toISOString() },
  { id: '2', name: 'New Starter Onboarding', status: 'running', progress: 30, current_step: 'Assign Training', started_date: new Date(Date.now() - 172800000).toISOString() },
  { id: '3', name: 'Quarterly Review', status: 'paused', progress: 50, current_step: 'Manager Approval', started_date: new Date(Date.now() - 604800000).toISOString() },
  { id: '4', name: 'Equipment Inspection', status: 'running', progress: 90, current_step: 'Final Report', started_date: new Date(Date.now() - 259200000).toISOString() },
];

const getStatusConfig = (status) => {
  const configs = {
    running: { label: 'Running', color: 'bg-green-100 text-green-800', icon: Play },
    paused: { label: 'Paused', color: 'bg-yellow-100 text-yellow-800', icon: Pause },
    completed: { label: 'Completed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
    failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  };
  return configs[status] || configs.running;
};

export function WorkflowsActiveTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setWorkflows(MOCK_WORKFLOWS);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadWorkflows = async () => {
      try {
        setLoading(true);

        const { data, error } = await supabase
          .from('workflow_runs')
          .select(`
            id,
            status,
            progress,
            current_step,
            started_date,
            workflows!inner(name, company_id)
          `)
          .eq('workflows.company_id', company.id)
          .in('status', ['running', 'paused'])
          .order('started_date', { ascending: false })
          .limit(10);

        if (error) throw error;

        const formattedData = (data || []).map(run => ({
          id: run.id,
          name: run.workflows?.name,
          status: run.status,
          progress: run.progress || 0,
          current_step: run.current_step,
          started_date: run.started_date
        }));

        setWorkflows(formattedData);
      } catch (error) {
        console.error('Failed to load workflows:', error);
      } finally {
        setLoading(false);
      }
    };

    loadWorkflows();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('workflow-runs')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'workflow_runs'
      }, () => loadWorkflows())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
      </div>
    );
  }

  if (workflows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <Workflow className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No active workflows</p>
        <p className="text-xs text-gray-500 mt-1">Start a workflow to see it here</p>
      </div>
    );
  }

  const runningCount = workflows.filter(w => w.status === 'running').length;
  const pausedCount = workflows.filter(w => w.status === 'paused').length;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Badge className="bg-violet-500 text-white">{workflows.length}</Badge>
          <span className="text-xs text-gray-600">active</span>
        </div>
        <div className="flex gap-1">
          {runningCount > 0 && (
            <Badge className="bg-green-100 text-green-800 text-[10px]">
              {runningCount} running
            </Badge>
          )}
          {pausedCount > 0 && (
            <Badge className="bg-yellow-100 text-yellow-800 text-[10px]">
              {pausedCount} paused
            </Badge>
          )}
        </div>
      </div>

      {/* Workflows list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {workflows.map((workflow) => {
            const statusConfig = getStatusConfig(workflow.status);
            const StatusIcon = statusConfig.icon;

            return (
              <div
                key={workflow.id}
                onClick={() => navigate(`/workflows/${workflow.id}`)}
                className="p-2 rounded-lg border bg-white hover:shadow-md cursor-pointer transition-all"
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {workflow.name}
                    </p>
                    {workflow.current_step && (
                      <p className="text-[10px] text-gray-500 truncate flex items-center gap-1">
                        <Clock className="h-2.5 w-2.5" />
                        {workflow.current_step}
                      </p>
                    )}
                  </div>
                  <Badge className={cn("text-[10px] shrink-0", statusConfig.color)}>
                    <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
                    {statusConfig.label}
                  </Badge>
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-2">
                  <Progress
                    value={workflow.progress}
                    className={cn(
                      "h-1.5 flex-1",
                      workflow.status === 'paused' ? "[&>div]:bg-yellow-500" : "[&>div]:bg-violet-500"
                    )}
                  />
                  <span className="text-[10px] text-gray-600 shrink-0">
                    {workflow.progress}%
                  </span>
                </div>

                {/* Started time */}
                <p className="text-[10px] text-gray-400 mt-1">
                  Started {formatDistanceToNow(parseISO(workflow.started_date), { addSuffix: true })}
                </p>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer */}
      <button
        onClick={() => navigate('/workflows')}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors shrink-0"
      >
        <Workflow className="h-3.5 w-3.5" />
        <span>Manage Workflows</span>
      </button>
    </div>
  );
}
